/** Platform accents for subscribe UI (badges & selected module rings). */
export const PROVIDER_THEME = {
  xero: {
    primary: "#0ea5e9",
    light: "#f0f9ff",
    ring: "ring-sky-400",
    badge: "bg-sky-50 text-sky-700 border-sky-200",
    label: "Xero",
  },
  qbo: {
    primary: "#16a34a",
    light: "#f0fdf4",
    ring: "ring-green-500",
    badge: "bg-green-50 text-green-800 border-green-200",
    label: "QuickBooks Online",
  },
};

export const SUBSCRIBE_MODULES = {
  account: {
    key: "accounts",
    label: "Account",
    description:
      "Essential account data, journals & attachments — always included",
    price: "$9.95/month",
    required: true,
    icon: "/assets/icons/organization.svg",
  },
  files: {
    key: "files",
    label: "Files",
    description: "Backup all files and documents from your ledger",
    price: "+$4/month",
    icon: "/assets/icons/file.svg",
  },
  history_and_notes: {
    key: "history_and_notes",
    label: "History and Notes",
    description: "Preserve your transaction history and notes",
    price: "+$4/month",
    icon: "/assets/icons/schedule.svg",
  },
  payroll: {
    key: "payroll",
    label: "Payroll",
    description: "Secure backup of payroll data and records",
    price: "+$0/month",
    icon: "/assets/icons/payment.svg",
  },
};

export const getProviderTheme = (provider) =>
  PROVIDER_THEME[provider] || PROVIDER_THEME.xero;

export const getSubscribeIntro = (provider) => {
  if (provider === "qbo") {
    return "Your QuickBooks Online plan includes the Account module.";
  }
  return "Your plan includes the Account module. Select additional modules below.";
};

/** Align with wowzer_normalize_xero_country_code (AU|CA|US|GB|NZ). */
export const normalizeXeroCountryCode = (countryCode) => {
  const c = String(countryCode ?? "")
    .trim()
    .toUpperCase();
  if (c === "UK") return "GB";
  if (["AU", "CA", "US", "GB", "NZ"].includes(c)) return c;
  return c;
};

/** Xero Payroll backup is available for Australia, United Kingdom, and New Zealand orgs. */
export const isPayrollSubscribeEligible = (countryCode) => {
  const c = normalizeXeroCountryCode(countryCode);
  return c === "AU" || c === "GB" || c === "NZ";
};

/** @returns {number} Monthly total in USD */
export const computeSubscribeMonthlyTotal = (formData) => {
  if (!formData?.accounts || formData?.skipped) {
    return 0;
  }
  let total = 9.95;
  if (formData.files) total += 4;
  if (formData.history_and_notes) total += 4;

  const retentionDays = Number(formData.retention_days) || 7;
  const extraRetentionDays = Math.max(0, retentionDays - 7);
  total += extraRetentionDays * 2.0;

  if (formData.coupon_discount) {
    if (formData.coupon_discount.percent_off != null) {
      total = total * (1 - Number(formData.coupon_discount.percent_off) / 100);
    } else if (formData.coupon_discount.amount_off != null) {
      total = Math.max(0, total - Number(formData.coupon_discount.amount_off));
    }
  }

  return total;
};

export const getDefaultOrgPlan = () => ({
  skipped: false,
  accounts: true,
  files: false,
  history_and_notes: false,
  payroll: false,
  retention_days: 7,
  coupon_code: "",
  coupon_discount: null,
});

/** @returns {'pending'|'included'|'skipped'} */
export const getOrgPlanStatus = (plan) => {
  if (!plan || plan.skipped || !plan.accounts) {
    return "skipped";
  }
  return "included";
};

export const formatPlanModuleSummary = (plan, provider = "xero", countryCode) => {
  if (!plan?.accounts || plan.skipped) {
    return "—";
  }
  const parts = ["Account"];
  if (provider !== "qbo") {
    if (plan.files) parts.push("Files");
    if (plan.history_and_notes) parts.push("History & Notes");
    if (plan.payroll && isPayrollSubscribeEligible(countryCode)) {
      parts.push("Payroll");
    }
  }
  const retentionDays = Number(plan.retention_days) || 7;
  if (retentionDays > 7) {
    parts.push(`${retentionDays}-day retention`);
  }
  return parts.join(", ");
};

/**
 * Whether an org can appear in the multi-subscribe wizard.
 */
export const isOrgEligibleForSubscribe = (org) => {
  const {
    is_vault_activated,
    is_trial_org,
    need_re_authentication,
    org_status,
    is_suscription_cancel_requested,
  } = org;

  if (need_re_authentication == 1) {
    return false;
  }
  // Allow resubscribe if cancellation was requested (user wants to change plan or resume)
  if (is_suscription_cancel_requested == 1) {
    return true;
  }
  if (is_trial_org == 1) {
    return false;
  }
  if (org_status == 1) {
    return false;
  }
  if (is_vault_activated == 1) {
    return true;
  }
  return org_status == 0 || org_status == null;
};

export const getOrgKey = (org) => {
  if (!org) return "";
  if (org.statusKey) return String(org.statusKey);
  const provider = org.provider ?? "xero";
  return `${provider}-${org.id}`;
};

export const buildInitialOrgPlans = (orgs, selectedOrgId = null) => {
  const plans = {};
  const hasTarget = selectedOrgId != null && selectedOrgId !== "";
  const hasMatchingTarget =
    hasTarget &&
    orgs.some(
      (org) =>
        String(org.id) === String(selectedOrgId) ||
        getOrgKey(org) === String(selectedOrgId) ||
        String(org.statusKey) === String(selectedOrgId)
    );

  orgs.forEach((org) => {
    const key = getOrgKey(org);
    if (hasMatchingTarget) {
      const isTarget =
        String(org.id) === String(selectedOrgId) ||
        key === String(selectedOrgId) ||
        String(org.statusKey) === String(selectedOrgId);

      plans[key] = isTarget
        ? getDefaultOrgPlan()
        : {
            ...getDefaultOrgPlan(),
            accounts: false,
            skipped: true,
          };
    } else {
      plans[key] = getDefaultOrgPlan();
    }
  });
  return plans;
};

export const buildBulkSubscribePayload = (orgs, orgPlans) =>
  orgs.map((org) => {
    const key = getOrgKey(org);
    const plan = orgPlans[key] ?? orgPlans[org.id] ?? getDefaultOrgPlan();
    const hasAccount = !!plan.accounts && !plan.skipped;
    return {
      org_id: Number(org.id),
      provider: org.provider ?? "xero",
      orgKey: key,
      skipped: plan.skipped || !plan.accounts,
      accounts: hasAccount,
      files: hasAccount && !!plan.files,
      history_and_notes: hasAccount && !!plan.history_and_notes,
      payroll: hasAccount && !!plan.payroll,
      retention_days: hasAccount ? (Number(plan.retention_days) || 7) : 7,
      coupon_code:
        hasAccount && plan.coupon_code
          ? String(plan.coupon_code).trim()
          : null,
    };
  });
